// Mijn werk: de eigen taken, met de markup van het prototype (mwRoot en MW_ROW in design/build/prototype.js), voor de
// medewerker en de planner. Nu doen (komende 14 dagen, te laat, later, achterstand), Geparkeerd en Afsluiten, als lijst
// of als kalender van vier werkweken op de deadline. De achterstand ruimt u in één keer op: een medewerker stelt nieuwe
// datums voor aan de planner, de planner zet ze meteen in Planner.
// Met Clockify: per rij de uren (gelogd tegenover de schatting) en een knop om de timer te starten, en rechts de lopende
// timer (of een voorstel om er een te starten), Mijn week (gelogd per dag tegenover de capaciteit, volgende week),
// Mijn support uit de supportmailbox en wie in het team afwezig is. Zonder Clockify vult de lijst de breedte.
// Een timer die al meer dan 12 u loopt, is wellicht vergeten: stoppen of een nieuwe starten zou al die uren boeken,
// dus dat gebeurt dan in Clockify zelf.
// Zonder Project-licentie (me.canWrite) is de lijst alleen-lezen met een link naar Planner; voorstellen kan wel. De
// timer hoort bij Clockify en werkt ook zonder licentie.
import { Fragment, useEffect, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { fmtHours, useCapacity, useClockifyLive, useTimer, type Absence, type ClockifyLive, type MyWeek, type Timer } from "../lib/api-clockify";
import { useSupport } from "../lib/api-support";
import { byUrgency, catOf, labelColor, labelFor } from "../lib/board";
import { count, isOpen, resumeOps, when } from "../lib/cleanup";
import { faseChain, padShort } from "../lib/fases";
import { absentText, andList, bezetting, dayRange, hoursText, thresholds, timerText, type Bezetting } from "../lib/health";
import { useIndex, useMe, usePlanning, useProposals, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays, ddmm, deadline, dow, mondayOf, plannerTaskUrl, priorityLabel, todayIn, type Deadline } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import { hoursSince, klantText, nOf, waitCls, waitText } from "../lib/support";
import type { Me, PlanningData, Proposal, Task, TaskOp } from "../lib/types";
import { OpruimenDrawer } from "../components/OpruimenDrawer";
import { TaskDrawer } from "../components/TaskDrawer";
import { TimerAsk } from "../components/TimerAsk";
import { timerOnTask } from "../components/TaskCard";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";

type Tab = "nu" | "park" | "close";
type Item = { t: Task; d: Deadline };
const MON = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const DOW = ["ma", "di", "wo", "do", "vr"];
const DAY = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const DAGEN = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];
const PROGRESS_1: TaskOp[] = [{ kind: "progress", progress: 1 }];
const CLOCKIFY_URL = "https://app.clockify.me/tracker";

interface RowProps {
  t: Task; mode: Tab; data: PlanningData; me: Me; sub: string; prop?: Proposal;
  /** uren uit Clockify; null: zonder Clockify (geen kolom) */
  hrs: ReturnType<typeof hoursText> | null;
  /** timerknop (enkel bij Nu doen); null: geen knop */
  play: { on: boolean; can: boolean; title: string; toggle: () => void } | null;
  onOpen: () => void; onFinish: () => void; onResume: () => void;
}

function MRow({ t, mode, data, me, sub, prop, hrs, play, onOpen, onFinish, onResume }: RowProps) {
  const isAct = catOf(t) === "actief", d = deadline(t.end, data.today), l = labelFor(t), write = me.canWrite;
  const finishTitle = mode === "close" ? "Afsluiten in Planner" : "Afwerken";
  return (
    <div className={"p-row wrap p-mrow" + (t.pending ? " pending" : "") + (play?.on ? " live" : "")}>
      {write && <button type="button" className="p-circ" title={finishTitle} aria-label={finishTitle} onClick={onFinish}><Icon name="check" size={12} /></button>}
      <button type="button" className={"p-mrow-main" + (hrs ? "" : " p-nohr")} onClick={onOpen}>
        <span className="main"><b>{t.title}</b><span>{sub}</span></span>
        <span className="lb">{l && <span className="p-lbl"><i style={{ background: labelColor(l) }} />{l}</span>}</span>
        <span className="dl">
          {isAct && t.critical && <span className="p-crit">Kritisch</span>}
          {isAct && t.priority <= 3 && <span className={"p-prio p" + t.priority}>{priorityLabel(t.priority)}</span>}
          <span className={isAct ? "p-dl" + (d.tone ? " t-" + d.tone : "") : "p-meta-i t-mute"}><Icon name={isAct && d.key === "verlopen" ? "alert" : "cal"} size={13} />{isAct ? d.text : ddmm(t.end)}</span>
          {prop && <span className="p-prop">voorstel {ddmm(prop.end)} · wacht op de planner</span>}
        </span>
        {hrs && <span className="hr"><span className={hrs.cls} title={hrs.title}><Icon name="clock" size={13} />{hrs.text}</span></span>}
      </button>
      {play && (
        <button type="button" className={"p-playbtn" + (play.on ? " on" : "") + (play.can ? "" : " p-dim")} title={play.title} aria-label={play.title} disabled={!play.can} onClick={play.toggle}>
          <Icon name={play.on ? "stop" : "play"} size={14} />
        </button>
      )}
      {mode === "park" && write && <button type="button" className="ex-btn-xs" onClick={onResume}>Oppakken</button>}
      {!write && <a className="ex-btn-xs" href={plannerTaskUrl(me, t)} target="_blank" rel="noreferrer" title="Deze taak openen in Planner"><Icon name="ext" size={13} />Openen in Planner</a>}
    </div>
  );
}

const two = (n: number) => (n < 10 ? "0" : "") + n;
const hms = (sec: number) => { sec = Math.max(0, Math.floor(sec)); return Math.floor(sec / 3600) + ":" + two(Math.floor((sec % 3600) / 60)) + ":" + two(sec % 60); };
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** De klok van de lopende timer; tikt elke seconde zonder de rest van het scherm opnieuw te tekenen. */
function Clock({ since }: { since: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const i = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(i); }, []);
  return <span className="p-clock">{hms((now - Date.parse(since)) / 1000)}</span>;
}

interface NowProps {
  live: ClockifyLive; me: Me; data: PlanningData; mine: Timer | null; next: Task | null;
  kName: (id: string) => string; onOpen: (id: string) => void; timer: ReturnType<typeof useTimer>;
  /** Timer starten: eerst de omschrijving (TimerAsk) */
  onStart: (t: Task) => void;
}

/** Nu bezig (prototype p-now): de lopende timer met klok en Stoppen, anders de eerstvolgende taak om te starten. */
function NowCard({ live, me, data, mine, next, kName, onOpen, timer, onStart }: NowProps) {
  const today = data.today;
  if (!live.linked.includes(me.id)) {
    return (
      <section className="p-card p-now"><div className="p-card-h"><div><h2>Geen timer</h2>
        <p>U staat niet in Clockify met uw e-mailadres{me.email ? " " + me.email : ""}. Zodra dat zo is, start u hier de timer en ziet u uw week.</p>
      </div></div></section>
    );
  }
  if (mine?.stale) {
    return (
      <section className="p-card p-now">
        <div className="p-card-h"><div><h2 className="t-warn">Timer loopt sinds {ddmm(todayIn("Europe/Brussels", new Date(mine.since)))}</h2><p>Wellicht vergeten. Stoppen vanuit de app boekt alle uren sindsdien: corrigeer hem in Clockify.</p></div></div>
        <div className="p-now-b">
          <span className="p-now-m">{timerText(mine)}</span>
          <div className="p-now-a"><a className="ex-btn" href={CLOCKIFY_URL} target="_blank" rel="noreferrer"><Icon name="ext" size={14} />Openen in Clockify</a></div>
        </div>
      </section>
    );
  }
  if (mine) {
    const tid = mine.taskId || mine.taskIds?.[0];  // een gedeelde Clockify-taak die niet in de app startte: de eerste taak
    const task = tid ? data.tasks.find((t) => t.id === tid) : null;
    const h = task ? live.tasks[task.id] : undefined, logged = h?.logged || 0;
    const meta = task
      ? (h?.estimate ? fmtHours(logged).replace(" u", "") + " van " + fmtHours(h.estimate) + " gelogd" : fmtHours(logged) + " gelogd · nog geen schatting") + " · " + deadline(task.end, today).long.toLowerCase()
      : mine.taskName && mine.description ? mine.description : "Niet gekoppeld aan een taak in Planner";
    return (
      <section className="p-card p-now">
        <div className="p-card-h p-now-h"><div><h2>Nu bezig</h2><p>Clockify · {mine.client || mine.project || "zonder klant"} · sinds {when(mine.since, today).replace(/^vandaag /, "")}</p></div><span className="ex-live">Live</span></div>
        <div className="p-now-b">
          {task
            ? <button type="button" className="p-now-t" title="Taak openen" onClick={() => onOpen(task.id)}>{task.title}</button>
            : <b className="p-now-t">{mine.taskName || mine.description || mine.project || "Zonder omschrijving"}</b>}
          <span className="p-now-m">{meta}</span>
          <div className="p-now-row"><Clock since={mine.since} /><button type="button" className="ex-btn ex-btn--primary" disabled={timer.busy} onClick={timer.stop}><Icon name="stop" size={14} />Stoppen</button></div>
        </div>
      </section>
    );
  }
  const day = live.week?.days.find((d) => d.date === today);
  const away = day?.absent && !day.cap ? day : null;
  const last = live.lastActive[me.id];
  return (
    <section className="p-card p-now">
      <div className="p-card-h"><div>
        <h2>{away ? "Afwezig" : "Geen timer actief"}</h2>
        <p>{away ? cap1(away.absent === "verlof" ? "verlof vandaag" : (away.label || away.absent) + " vandaag") : last ? "Laatst gelogd " + when(last, today) : "Nog niets gelogd in Clockify"}</p>
      </div></div>
      {next && !away && (
        <div className="p-now-b">
          <span className="p-now-m">Eerstvolgende taak</span>
          <button type="button" className="p-now-t" title="Taak openen" onClick={() => onOpen(next.id)}>{next.title}</button>
          <span className="p-now-m">{kName(next.projectId)} · {deadline(next.end, today).text}</span>
          <div className="p-now-a"><button type="button" className="ex-btn ex-btn--primary" disabled={timer.busy} onClick={() => onStart(next)}><Icon name="play" size={14} />Timer starten</button></div>
        </div>
      )}
    </section>
  );
}

/** Mijn week: per dag gelogd tegenover de capaciteit (de stippellijn), dagen met te weinig uren in amber; daaronder
 *  de bezetting volgende week als de capaciteit beschikbaar is. */
function WeekCard({ week, today, next, nextAbsent, med }: { week: MyWeek; today: string; next: Bezetting | null; nextAbsent: Absence[]; med: boolean }) {
  const days = week.days.filter((d, i) => i < 5 || d.cap > 0 || d.logged > 0);
  const scale = Math.max(9, ...days.map((d) => Math.max(d.cap, d.logged)));
  const px = (h: number) => Math.round(Math.min(1, h / scale) * 56);
  const leave = (d: MyWeek["days"][number]) => !!d.absent && !d.cap;
  const short = days.filter((d) => d.date < today && !leave(d) && d.cap > 0 && d.logged < d.cap - 0.3);
  const todayDay = days.find((d) => d.date === today);
  const sub = fmtHours(week.logged) + " van " + fmtHours(week.cap) + " gelogd" + (todayDay && todayDay.cap > 0 ? " · " + DAGEN[dow(today)] + " loopt nog" : "");
  const nextText = !next ? "" : next.pct == null
    ? cap1(nextAbsent.map((a) => absentText(a, today)).join(", ") || "afwezig")
    : fmtHours(next.load) + " van " + fmtHours(next.cap) + " gepland" + (next.tone === "overvol" ? (med ? " · bespreek met de planner" : " · te veel gepland") : "");
  return (
    <section className="p-card">
      <div className="p-card-h"><div><h2>Mijn week</h2><p>{sub}</p></div></div>
      <div className="p-days">
        {days.map((d) => {
          const isShort = short.includes(d), off = leave(d), future = d.date > today;
          const tip = off ? d.label || d.absent || "" : isShort ? "Nog " + fmtHours(d.cap - d.logged) + " aan te vullen" : fmtHours(d.logged) + " van " + fmtHours(d.cap);
          const cls = "p-day" + (d.date === today ? " today" : "") + (isShort ? " short" : "") + (off ? " leave" : "") + (future ? " future" : "") + (d.cap ? "" : " nocap");
          return (
            <div key={d.date} className={cls} title={tip}>
              <span className="v">{off ? d.absent : future && !d.logged ? "" : fmtHours(d.logged).replace(" u", "")}</span>
              <span className="bar" style={{ "--capb": px(d.cap) + "px" } as CSSProperties}><i style={{ height: px(d.logged) + "px" }} /></span>
              <span className="d">{DAY[dow(d.date)]}</span>
            </div>
          );
        })}
      </div>
      {short.length > 0 && <p className="p-week-note t-warn">{cap1(andList(short.map((d) => DAGEN[dow(d.date)])))}: nog {fmtHours(short.reduce((a, d) => a + d.cap - d.logged, 0))} aan te vullen in Clockify</p>}
      {next ? <>
        <div className="p-week-next"><span>Volgende week</span><span className={next.barCls}><i style={{ width: next.width + "%" }} /></span><b className={next.txtCls}>{next.text}</b></div>
        <p className="p-week-note p-week-last">{nextText}</p>
      </> : <div className="p-week-last" />}
    </section>
  );
}

/** Verlof in deze en volgende week, over een weekend heen samengevoegd; enkel wat nog niet voorbij is. Feestdagen en
 *  sluitingsdagen gelden voor iedereen en staan er niet bij. */
function leaveSpans(weeks: { absent: Absence[] }[], today: string): Absence[] {
  const out: Absence[] = [];
  for (const a of weeks.slice(0, 2).flatMap((w) => w.absent).filter((a) => a.kind === "verlof").sort((x, y) => x.from.localeCompare(y.from))) {
    const last = out[out.length - 1];
    if (last && a.from <= addDays(last.to, 3)) { if (a.to > last.to) last.to = a.to; } else out.push({ ...a });
  }
  return out.filter((a) => a.to >= today);
}

export function MijnWerk() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const { data: proposals } = useProposals();
  const idx = useIndex(data);
  const { klant } = useAppState();
  const ops = useTaskOps();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("nu");
  const [view, setView] = useState<"lijst" | "kalender">("lijst");
  const [calWeek, setCalWeek] = useState(0);
  const [calOpen, setCalOpen] = useState<Record<string, boolean>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [ask, setAsk] = useState<Task | null>(null); // Timer starten: waaraan werkt u?
  const sel = params.get("taak");
  const { data: liveData } = useClockifyLive();
  const { data: settings } = useSettings();
  const mayCap = me?.role === "planner" || rightsOf(settings?.settings).seeCapacity;
  const { data: cap } = useCapacity(!!me && mayCap);
  const { data: support } = useSupport(null);
  const timer = useTimer();

  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;
  const today = data.today, med = me.role === "medewerker", write = me.canWrite;
  // Clockify: zonder koppeling (of zolang het niet geladen is) geen uren, timer en zijkolom
  const live = liveData && liveData.clockify.state !== "uit" ? liveData : null;
  const inClockify = !!live && live.linked.includes(me.id);
  const myTimer = live?.timers.find((t) => t.personId === me.id) || null;
  const running = myTimer && !myTimer.stale ? myTimer : null;
  // de timer loopt enkel op een Clockify-taak: zonder taak in Clockify logt de app geen tijd
  const ckLinked = (t: Task) => { const h = live?.tasks[t.id]; return !!h && !!h.clockifyTaskId && !h.none && !h.missing; };
  const playOf = (t: Task) => {
    if (!inClockify) return null;
    const on = !!running && timerOnTask(running, t);
    const title = on ? "Timer stoppen" : myTimer?.stale ? "Uw timer loopt al sinds " + ddmm(todayIn("Europe/Brussels", new Date(myTimer.since))) + ": corrigeer die eerst in Clockify"
      : ckLinked(t) ? (running ? "Timer starten (de lopende stopt)" : "Timer starten") : "Nog geen Clockify-taak: maak ze aan in de taak";
    return { on, can: !timer.busy && (on || (!myTimer?.stale && ckLinked(t))), title, toggle: () => (on ? timer.stop() : setAsk(t)) };
  };

  const kName = (id: string) => idx.klanten.get(id)?.name || "";
  const withD = (t: Task): Item => ({ t, d: deadline(t.end, today) });
  const mine = data.tasks.filter((t) => isOpen(t) && t.owners.some((o) => o.personId === me.id) && (!klant || t.projectId === klant));
  const act = mine.filter((t) => catOf(t) === "actief").map(withD).sort(byUrgency);
  const parked = mine.filter((t) => catOf(t) === "geparkeerd").map(withD).sort(byUrgency);
  const closing = mine.filter((t) => catOf(t) === "afsluiten").map(withD).sort((a, b) => kName(a.t.projectId).localeCompare(kName(b.t.projectId)) || a.t.title.localeCompare(b.t.title));
  const propOf = new Map((proposals || []).map((p) => [p.taskId, p]));
  const old = act.filter((x) => x.d.key === "verlopen");
  const oldOpen = old.filter((x) => !propOf.has(x.t.id));
  const nProp = old.length - oldOpen.length;
  const over = act.filter((x) => x.d.od >= 1).length;
  const canBulk = med || write; // voorstellen blijft in de app; de planner schrijft naar Planner

  const how = med ? (write ? "Stel nieuwe datums voor, werk ze af of parkeer ze in één keer." : "Stel nieuwe datums voor in één keer; afwerken en parkeren doet u in Planner.")
    : write ? "Kies nieuwe datums, werk ze af of parkeer ze in één keer." : "Kies nieuwe datums, werk ze af of parkeer ze in Planner.";
  const oldText = oldOpen.length
    ? count(oldOpen.length) + " met een deadline die al meer dan 30 dagen voorbij is. " + how + (nProp ? " " + nProp + (nProp === 1 ? " voorstel wacht" : " voorstellen wachten") + " op de planner." : "")
    : "Voor alle taken in de achterstand wacht een voorstel op de planner.";
  const sections = [
    { title: "Komende 14 dagen", items: act.filter((x) => x.d.od <= 0 && x.d.od >= -14), old: false },
    { title: "Te laat", items: act.filter((x) => x.d.key === "telaat"), old: false },
    { title: "Later", items: act.filter((x) => x.d.od < -14), old: false },
    { title: "Achterstand · deadline meer dan 30 dagen voorbij", items: old, old: true },
  ].filter((s) => s.items.length);

  const openTask = (id: string) => setParams((p) => { p.set("taak", id); return p; });
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });
  const openBulk = () => { closeTask(); setBulkOpen(true); };
  const sub = (t: Task) => {
    const done = t.checklist.filter((c) => c.done).length;
    return [kName(t.projectId), padShort(faseChain(t.faseId, idx.fases), 48) || null, t.checklist.length ? "checklist " + done + "/" + t.checklist.length : null].filter(Boolean).join(" · ");
  };
  const row = (x: Item, mode: Tab) => (
    <MRow key={x.t.id} t={x.t} mode={mode} data={data} me={me} sub={sub(x.t)} prop={propOf.get(x.t.id)} onOpen={() => openTask(x.t.id)}
      hrs={live ? hoursText(live.tasks[x.t.id]) : null} play={mode === "nu" ? playOf(x.t) : null}
      onFinish={() => ops.run(x.t, PROGRESS_1, (mode === "close" ? "Afgesloten in Planner: " : "Afgewerkt: ") + x.t.title).catch(() => {})}
      onResume={() => { const o = resumeOps(x.t); if (o.length) ops.run(x.t, o, "Weer opgepakt: " + x.t.title).catch(() => {}); }} />
  );
  const closeAll = () => ops.runMany(closing.map((x) => ({ task: x.t, ops: PROGRESS_1 })), closing.length + " taken afgesloten in Planner");

  // kalender: vier werkweken vanaf de gekozen week; de taken van het tabblad staan op hun deadline, die van het
  // weekend op vrijdag
  const calTasks = (tab === "nu" ? act : tab === "park" ? parked : closing).map((x) => x.t);
  const from = addDays(mondayOf(today), 7 * calWeek);
  const days: string[] = [];
  for (let w = 0; w < 4; w++) for (let i = 0; i < 5; i++) days.push(addDays(from, w * 7 + i));
  const last = days[days.length - 1];
  const before = calTasks.filter((t) => t.end < from).length, after = calTasks.filter((t) => t.end > addDays(last, 2)).length;
  const tabs: [Tab, string, number][] = [["nu", "Nu doen", act.length], ["park", "Geparkeerd", parked.length], ["close", "Afsluiten", closing.length]];
  const tabName = tabs.find((x) => x[0] === tab)![1];
  const selTask = sel ? data.tasks.find((t) => t.id === sel) : null;

  // zijkolom: eerstvolgende taak voor de timer, support op mijn naam (supportmailbox), volgende week en wie er in het
  // team afwezig is (verlof deze en volgende week, uit de capaciteit)
  const next = act.map((x) => x.t).find((t) => ckLinked(t) && !(running && timerOnTask(running, t))) || null;
  const supOk = !!support && support.sync.state !== "uit" && !!support.sync.lastOk;
  const mySup = supOk ? support!.open.filter((c) => c.devs.some((x) => x.personId === me.id) && (!klant || c.klantId === klant)) : [];
  const myCap = cap?.people.find((c) => c.personId === me.id) || null;
  const nextWeek = myCap?.clockifyUserId ? bezetting(myCap, 1, thresholds(settings?.settings)) : null;
  const away = (cap?.people || []).filter((c) => c.personId !== me.id && c.clockifyUserId)
    .flatMap((c) => leaveSpans(c.weeks, today).map((a) => ({ id: c.personId, a }))).sort((x, y) => x.a.from.localeCompare(y.a.from));
  const awayWhen = (a: Absence) => (a.from <= today ? "t/m " + ddmm(a.to) : a.from === a.to ? "op " + ddmm(a.from) : dayRange(a.from, a.to));

  return (
    <>
      <div className="p-head">
        <div><h1>Mijn werk</h1><p>{me.name} · {act.length} actieve taken · {over} over deadline{supOk ? " · " + nOf(mySup.length, "supportgesprek", "supportgesprekken") : ""}</p></div>
        <div className="p-tools">
          <div className="ex-seg" role="group" aria-label="Weergave">
            <button type="button" aria-pressed={view === "lijst"} onClick={() => setView("lijst")}>Lijst</button>
            <button type="button" aria-pressed={view === "kalender"} onClick={() => setView("kalender")}>Kalender</button>
          </div>
        </div>
      </div>
      <div className={"p-mw" + (live ? "" : " p-mw-solo")}>
        <section className="p-card p-mw-list">
          <div className="p-mw-tabs">
            <div className="ex-tabs p-tabs" role="tablist">
              {tabs.map(([k, label, n]) => <button key={k} type="button" role="tab" className="ex-tab" aria-selected={tab === k} onClick={() => setTab(k)}>{label}<span className="p-tabn">{n}</span></button>)}
            </div>
            {tab === "close" && closing.length > 1 && write && <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={closeAll}><Icon name="check" size={13} />Alle {closing.length} afsluiten</button>}
          </div>
          <div className="p-mw-body">
            {view === "kalender" && (
              <div className="p-cal">
                <div className="p-cal-bar">
                  <span className="p-cal-t">{ddmm(from)} tot {ddmm(last)} · {tabName} op de deadline</span>
                  <span className="p-cal-nav">
                    {before > 0 && <button type="button" className="p-cal-note" onClick={() => setView("lijst")}><Icon name="alert" size={13} />{count(before)} met een eerdere deadline</button>}
                    <button type="button" className="ex-btn-xs" onClick={() => setCalWeek(calWeek - 1)}>Vorige week</button>
                    <button type="button" className="ex-btn-xs" onClick={() => setCalWeek(0)}>Deze week</button>
                    <button type="button" className="ex-btn-xs" onClick={() => setCalWeek(calWeek + 1)}>Volgende week</button>
                  </span>
                </div>
                <div className="p-cal-hd">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
                <div className="p-cal-g">
                  {days.map((day, i) => {
                    const items = calTasks.filter((t) => t.end === day || (i % 5 === 4 && t.end > day && t.end <= addDays(day, 2)));
                    const opened = !!calOpen[day], first = i === 0 || day.slice(8) === "01";
                    return (
                      <div key={day} className={"p-cal-d" + (day === today ? " today" : "") + (day < today ? " past" : "")}>
                        <div className="p-cal-dh"><b>{Number(day.slice(8)) + (first ? " " + MON[Number(day.slice(5, 7)) - 1] : "")}</b>{day === today && <small>vandaag</small>}</div>
                        {(opened ? items : items.slice(0, 3)).map((t) => {
                          const d = deadline(t.end, today);
                          return (
                            <button key={t.id} type="button" className={"p-cal-i" + (tab === "nu" && t.critical ? " crit" : "") + (running && timerOnTask(running, t) ? " live" : "") + (tab === "nu" && d.od >= 0 ? " due" : "")} title={t.title + " · " + kName(t.projectId) + " · " + d.long} onClick={() => openTask(t.id)}>
                              <b>{t.title}</b><small>{kName(t.projectId)}</small>
                            </button>
                          );
                        })}
                        {items.length > 3 && !opened && <button type="button" className="p-cal-more" onClick={() => setCalOpen({ ...calOpen, [day]: true })}>+{items.length - 3} meer</button>}
                      </div>
                    );
                  })}
                </div>
                {after > 0 && <p className="p-cal-foot">{count(after)} later · <button type="button" className="ex-link" onClick={() => setCalWeek(calWeek + 1)}>verder kijken</button></p>}
              </div>
            )}
            {view === "lijst" && tab === "nu" && <>
              {sections.map((s) => (
                <Fragment key={s.title}>
                  <div className="p-sec"><span>{s.title}</span><span>{count(s.items.length)}</span></div>
                  {s.old && (
                    <div className="p-mw-old"><div className="ex-notice ex-notice--warning p-notice"><Icon name="alert" size={16} /><p>{oldText}</p>
                      {canBulk && oldOpen.length > 0 && <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={openBulk}>Achterstand opruimen</button>}
                    </div></div>
                  )}
                  {s.items.map((x) => row(x, "nu"))}
                </Fragment>
              ))}
              {!act.length && <p className="p-empty p-mw-empty">Geen actieve taken. Nieuw werk maakt u aan met Nieuwe taak.</p>}
            </>}
            {view === "lijst" && tab === "park" && <>
              <div className="p-sec"><span>Label On hold of Waiting</span><span>{write ? "Oppakken haalt het label weg" : "Oppakken doet u in Planner"}</span></div>
              {parked.map((x) => row(x, "park"))}
              {!parked.length && <p className="p-empty p-mw-empty">Niets geparkeerd.</p>}
            </>}
            {view === "lijst" && tab === "close" && <>
              <div className="p-sec"><span>Label Prod of Test validated</span><span>{write ? "Het rondje sluit de taak af in Planner" : "Afsluiten doet u in Planner"}</span></div>
              {closing.map((x) => row(x, "close"))}
              {!closing.length && <p className="p-empty p-mw-empty">Niets af te sluiten.</p>}
            </>}
          </div>
        </section>
        {live && (
          <div className="p-mw-side">
            <NowCard live={live} me={me} data={data} mine={myTimer} next={next} kName={kName} onOpen={openTask} timer={timer} onStart={setAsk} />
            {inClockify && live.week && <WeekCard week={live.week} today={today} next={nextWeek} nextAbsent={myCap?.weeks[1]?.absent || []} med={med} />}
            {supOk && (
              <section className="p-card">
                <div className="p-card-h"><div><h2>Mijn support</h2><p>{mySup.length} open · uit {support!.sync.mailbox}</p></div></div>
                {mySup.slice(0, 6).map((c) => {
                  const h = hoursSince(c.openSince);
                  return (
                    <div key={c.id} className="p-row p-sup">
                      <div className="main"><b title={c.subject}>{c.subject}</b><span>{klantText(c)} · {nOf(c.messages, "bericht", "berichten")}</span></div>
                      <span className={"p-wait " + waitCls(h)} title="Wacht sinds de oudste mail in de Inbox">{waitText(h)}</span>
                    </div>
                  );
                })}
                {mySup.length > 6 && <p className="p-week-note p-week-last">Nog {mySup.length - 6} in Support</p>}
                {!mySup.length && <p className="p-empty p-sup-empty">Geen open supportgesprekken op uw naam.</p>}
              </section>
            )}
            {away.length > 0 && (
              <div className="ex-notice ex-notice--mist p-notice"><Icon name="moon" size={16} />
                <p>Afwezig in het team: {away.map(({ id, a }, i) => <Fragment key={id + a.from}>{i ? " · " : ""}<b>{idx.people.get(id)?.first || "iemand"}</b> {awayWhen(a)}</Fragment>)}</p>
              </div>
            )}
          </div>
        )}
      </div>
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
      {ask && <TimerAsk task={ask} sub={kName(ask.projectId)} busy={timer.busy} onClose={() => setAsk(null)}
        options={(live?.tasks[ask.id]?.tasks || []).filter((c) => !c.missing).map((c) => ({ id: c.clockifyTaskId, name: c.name || "" }))}
        onStart={(d, ck) => timer.start(ask.id, ask.title, d, ck)} />}
      {bulkOpen && !selTask && <OpruimenDrawer items={oldOpen} data={data} klantName={kName} propose={med} write={write} onClose={() => setBulkOpen(false)} />}
    </>
  );
}
